import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuCheck } from "react-icons/lu";
import { MdErrorOutline } from "react-icons/md";
import { Button } from "antd";
import toast from "react-hot-toast";
import ConfirmationModal from "../../../../components/global/modal/ConfirmationModal.jsx";
import { ReviewCorporateDocumentsApi } from "../../../../api/request/corporate.js";
import { handleApiError } from "../../../../utils/handleApiError.js";

const ApplicationStatus = ({
  isReviewMode = false,
  reviewStatus = "Pending",
  userId,
  onReviewCompleted = () => {},
}) => {
  const navigate = useNavigate();
  const canReviewAction = isReviewMode && reviewStatus === "Pending";
  const isApprovedView = isReviewMode && reviewStatus === "Approved";
  const isRejectedView = isReviewMode && reviewStatus === "Rejected";
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isReviewLoading, setIsReviewLoading] = useState(false);

  const handleReview = async (status) => {
    if (!userId) return;
    if (status === 2 && !rejectionReason.trim()) {
      toast.error("Please enter rejection reason.");
      return;
    }
    try {
      setIsReviewLoading(true);
      const payload = {
        userId,
        status,
        ...(status === 2 ? { rejectionReason: rejectionReason.trim() } : {}),
      };
      const { data } = await ReviewCorporateDocumentsApi(payload);
      if (data?.success) {
        toast.success(data?.message || "Review submitted successfully.");
        setIsAcceptModalOpen(false);
        setIsRejectModalOpen(false);
        setRejectionReason("");
        onReviewCompleted(status);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsReviewLoading(false);
    }
  };

  return (
    <div className="flex flex-col justify-center items-center min-h-[400px]">
      <div
        className={`flex items-center justify-center p-4 mb-6 border-4 rounded-full ${
          isRejectedView ? "border-red-500" : "border-green-500"
        }`}
      >
        {isRejectedView ? (
          <MdErrorOutline size={50} className="text-red-500" />
        ) : (
          <LuCheck size={50} className="text-green-500" />
        )}
      </div>

      <h1 className="text-3xl font-semibold primary mb-3">
        {isReviewMode
          ? isApprovedView
            ? "Corporate Application Approved"
            : isRejectedView
            ? "Corporate Application Rejected"
            : "Corporate Application Review"
          : "Thank You For Submitting"}
      </h1>

      <p className="text-lg font-medium mb-3">
        {isReviewMode
          ? isApprovedView
            ? "This corporate onboarding application is approved"
            : isRejectedView
            ? "This corporate onboarding application is rejected"
            : "Review this corporate onboarding application"
          : "Your Live Trading Account Application"}
      </p>

      <p className="text-gray-500 mb-6">
        {isReviewMode
          ? isApprovedView
            ? "This application has already been accepted."
            : isRejectedView
            ? "This application has already been rejected."
            : "Approve to accept this application, or reject with a reason."
          : "We will carefully review your application and notify you once your account is approved."}
      </p>

      {canReviewAction ? (
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => setIsAcceptModalOpen(true)}
            className="bg-[#2D9B63] text-sm text-white font-medium uppercase px-10 py-2 rounded-lg hover:bg-[#096034] duration-500"
          >
            Accept
          </button>
          <button
            type="button"
            onClick={() => setIsRejectModalOpen(true)}
            className="bg-[#D91819] text-sm text-white font-medium uppercase px-10 py-2 rounded-lg hover:bg-[#aa0001] duration-500"
          >
            Reject
          </button>
        </div>
      ) : isReviewMode ? (
        <p className="text-gray-500">
          {isApprovedView
            ? "This application is already accepted."
            : "This application is already rejected."}
        </p>
      ) : (
        <Button
          type="primary"
          size="large"
          onClick={() => navigate("/dashboard")}
        >
          Go to Dashboard
        </Button>
      )}

      <ConfirmationModal
        isOpen={isAcceptModalOpen}
        setIsOpen={setIsAcceptModalOpen}
        type="success"
        message="Are you sure you want to accept this corporate application?"
        onConfirm={() => handleReview(1)}
        isLoading={isReviewLoading}
        loadingLabel="Accepting..."
      />

      <ConfirmationModal
        isOpen={isRejectModalOpen}
        setIsOpen={setIsRejectModalOpen}
        type="reject"
        message="Are you sure you want to reject this corporate application?"
        onConfirm={() => handleReview(2)}
        isLoading={isReviewLoading}
        loadingLabel="Rejecting..."
        textArea
        placeholder="Enter rejection reason"
        textAreaValue={rejectionReason}
        setTextAreaValue={setRejectionReason}
      />
    </div>
  );
};

export default ApplicationStatus;
